import React, { useEffect } from "react";
import mtz from "moment-timezone";
import { Box, Paper, SelectChangeEvent } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import styled from "@mui/system/styled";

import { AccountComplianceStatusSelect } from "../../../../components/ui";

import usePagination from "../../../../hooks/usePagination";
import { EVALUATIONS_API_URLS, EvaluationsAPI } from "../../../../api/evaluations";
import { fundedTraderActions } from "../../../../reducers/fundedTrader";
import { DATE_FORMAT_hhmmssAzz } from "../../../../constants";
import { PASSES_STATUS_OPTIONS } from "./constants";
import { ContainerWithLoader, PaginationWithLimit, Select } from "../../../../components/ui";
import s from "./styles.module.css";

const TableGridItem = styled("div")({
  display: "flex",
  alignItems: "center",
});

const PassesHistory = (): React.ReactElement => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const pagination = usePagination(5);

  const handleChangePage = (page: number) => pagination.setPage(page);

  const {
    isLoading,
    id,
    accounts_id,
    evaluationCompletedHistory: { data, isLoading: isLoadingEvaluationCompletedHistory },
  } = useSelector((state) => state.fundedTrader);

  const fetchEvaluationCompleteHistory = useQuery({
    enabled: !isLoading && !!id,
    queryKey: [
      EVALUATIONS_API_URLS.customerEvaluationCompleteHistory,
      id,
      pagination.limit,
      pagination.page,
    ],
    queryFn: async () => {
      dispatch(fundedTraderActions.evaluationCompletedHistoryFetchRequest());

      const query = new URLSearchParams();
      query.set("funded_trader_id", id.toString());
      query.set("page", pagination.page.toString());
      query.set("limit", pagination.limit.toString());
      const response = await EvaluationsAPI.getCustomerEvaluationCompleteHistoryRequest(
        query.toString()
      );
      pagination.setCount(response.data.count);
      dispatch(fundedTraderActions.evaluationCompletedHistoryFetchSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (fetchEvaluationCompleteHistory.isError && fetchEvaluationCompleteHistory.error) {
      console.error("Error fetching funded trader data", fetchEvaluationCompleteHistory.error);
      dispatch(fundedTraderActions.evaluationCompletedHistoryFetchFailed());
    }
  }, [fetchEvaluationCompleteHistory.isError, fetchEvaluationCompleteHistory.error]);

  const handleUpdatePassStatus = useMutation({
    mutationKey: [EVALUATIONS_API_URLS.customerEvaluationCompleteHistory, id],
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const response = await EvaluationsAPI.updateCustomerEvaluationCompleteHistoryRequest({
        id,
        status,
        accounts_id,
      });
      return response;
    },
    onSuccess: () => {
      fetchEvaluationCompleteHistory.refetch();
      queryClient.invalidateQueries({
        queryKey: [
          EVALUATIONS_API_URLS.customerEvaluationCompleteHistory,
          "get-active-passed-accounts",
          accounts_id,
        ],
      });
    },
  });

  useEffect(() => {
    pagination.setPage(0);
  }, [pagination.limit]);

  return (
    <Paper sx={{ padding: "14px", backgroundColor: "#f2f2f2" }}>
      <Box display="flex" alignItems="center" m="10px 0">
        <b>Passes</b>
      </Box>
      <ContainerWithLoader isLoading={isLoadingEvaluationCompletedHistory} report={data}>
        <Box
          display="grid"
          gridTemplateColumns="auto auto auto auto auto auto"
          sx={{ gridGap: "3px" }}
        >
          <div>
            <b>Date</b>
          </div>
          <div>
            <b>Account name</b>
          </div>
          <div>
            <b>Email</b>
          </div>
          <div>
            <b>Type & Size</b>
          </div>
          <div>
            <b>Compliance status</b>
          </div>
          <div>
            <b>Status</b>
          </div>

          {data.map((d) => (
            <React.Fragment key={d.id}>
              <TableGridItem>
                {mtz(d.timestamp).tz("America/Chicago").format(DATE_FORMAT_hhmmssAzz)}
              </TableGridItem>
              <TableGridItem>{d.e2t_account_id}</TableGridItem>
              <TableGridItem>{d.email}</TableGridItem>
              <TableGridItem>{d.type_and_size}</TableGridItem>
              <div>
                <AccountComplianceStatusSelect status={d.compliance_status || ""} id={d.id} />
              </div>
              <div>
                <Select
                  baseSelectStyles={{
                    className: s.baseSelect,
                    classes: {
                      root: s.rootBaseSelect,
                    },
                  }}
                  disabled={handleUpdatePassStatus.isPending}
                  size="small"
                  options={PASSES_STATUS_OPTIONS}
                  value={d.status || ""}
                  onChange={(event: SelectChangeEvent<string>) =>
                    handleUpdatePassStatus.mutate({
                      id: d.id,
                      status: event.target.value as string,
                    })
                  }
                />
              </div>
            </React.Fragment>
          ))}
        </Box>
      </ContainerWithLoader>
      <PaginationWithLimit
        count={pagination.count}
        limit={pagination.limit}
        setLimit={pagination.setLimit}
        page={pagination.page}
        setPageState={handleChangePage}
      />
    </Paper>
  );
};

export default PassesHistory;
